import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface TaskSubmission {
  _id: string;
  submissionText: string;
  proofFiles: {
    filename: string;
    originalName: string;
    mimetype: string;
    size: number;
    uploadDate: string;
  }[];
  status: 'pending' | 'approved' | 'rejected' | 'under_review';
  reviewedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  reviewDate?: string;
  reviewNotes?: string;
  rewardAmount: number;
  rewardStatus: 'pending' | 'credited' | 'withheld';
  submissionDate: string;
  completionTime?: number;
  taskId: {
    _id: string;
    title: string;
    category: string;
    rewardAmount: number;
    priority: string;
  };
  userId: {
    _id: string;
    name: string;
    email: string;
    userType: string;
  } | null;
}

interface SubmissionStats {
  _id: string;
  count: number;
  totalReward: number;
}

const AdminTaskReviewDashboard: React.FC = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
  const [stats, setStats] = useState<SubmissionStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('submissionDate');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Review Modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<TaskSubmission | null>(null);
  const [reviewStatus, setReviewStatus] = useState<string>('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // File Preview Modal
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [selectedFileBlob, setSelectedFileBlob] = useState<string>('');
  const [fileLoading, setFileLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_BASE_URL}/task-submissions/admin/all`, {
        params: {
          page: currentPage,
          limit: 20,
          status: statusFilter === 'all' ? undefined : statusFilter,
          sortBy,
          sortOrder
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setSubmissions(response.data.submissions);
      setStats(response.data.stats);
      setTotalPages(response.data.pagination.pages);
    } catch (error: any) {
      setError('Failed to load submissions. Please try again.');
      console.error('Load submissions error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, sortBy, sortOrder, API_BASE_URL]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadSubmissions();
    }
  }, [user, currentPage, statusFilter, sortBy, sortOrder, loadSubmissions]);

  const handleReview = async () => {
    if (!selectedSubmission || !reviewStatus) return;
    
    setReviewLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(
        `${API_BASE_URL}/task-submissions/admin/review/${selectedSubmission._id}`,
        {
          status: reviewStatus,
          reviewNotes: reviewNotes.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setSuccess(`Submission ${reviewStatus} successfully!`);
      setShowReviewModal(false);
      
      // Reset review form
      setReviewStatus('');
      setReviewNotes('');
      setSelectedSubmission(null);
      
      // Reload submissions
      loadSubmissions();
      
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to review submission');
    } finally {
      setReviewLoading(false);
    }
  };

  const openReviewModal = (submission: TaskSubmission) => {
    setSelectedSubmission(submission);
    setReviewStatus(submission.status === 'pending' ? 'approved' : submission.status);
    setReviewNotes(submission.reviewNotes || '');
    setShowReviewModal(true);
  };

  const openFilePreview = async (filename: string) => {
    setFileLoading(true);
    setSelectedFile('');
    setSelectedFileBlob('');
    setShowFileModal(true);
    
    try {
      const response = await axios.get(`${API_BASE_URL}/task-submissions/files/${filename}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);
      setSelectedFileBlob(blobUrl);
      setSelectedFile(filename);
    } catch (error: any) {
      console.error('File fetch error:', error);
      setError('Failed to load file preview');
    } finally {
      setFileLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
      under_review: 'info'
    };
    return <Badge bg={variants[status as keyof typeof variants] || 'secondary'}>
      {status.replace('_', ' ').toUpperCase()}
    </Badge>;
  };

  const getUserTypeBadge = (userType: string) => {
    const variants = {
      free: 'secondary',
      pro: 'warning',
      admin: 'danger'
    };
    return <Badge bg={variants[userType as keyof typeof variants] || 'secondary'} className="small">
      {userType.toUpperCase()}
    </Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const variants = {
      low: 'secondary',
      medium: 'primary',
      high: 'warning',
      urgent: 'danger'
    };
    return <Badge bg={variants[priority as keyof typeof variants] || 'secondary'} className="small">
      {priority.toUpperCase()}
    </Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatsCard = (status: string, statData: SubmissionStats | undefined) => {
    const count = statData?.count || 0;
    const totalReward = statData?.totalReward || 0;
    
    const colors = {
      pending: { bg: 'warning', icon: 'clock' },
      approved: { bg: 'success', icon: 'check-circle' },
      rejected: { bg: 'danger', icon: 'times-circle' },
      under_review: { bg: 'info', icon: 'eye' }
    };
    
    const color = colors[status as keyof typeof colors] || { bg: 'secondary', icon: 'question' };
    
    return (
      <Card className="h-100">
        <Card.Body className="text-center">
          <i className={`fas fa-${color.icon} text-${color.bg} mb-2`} style={{ fontSize: '2rem' }}></i>
          <h3 className="mb-1">{count}</h3>
          <p className="text-muted mb-1">{status.replace('_', ' ').toUpperCase()}</p>
          <small className="text-muted">₹{totalReward.toLocaleString()} total</small>
        </Card.Body>
      </Card>
    );
  };

  if (user?.role !== 'admin') {
    return (
      <Alert variant="danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        Access denied. Admin privileges required.
      </Alert>
    );
  }

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <h1><i className="fas fa-clipboard-check me-2"></i>Task Submission Review</h1>
          <p className="text-muted">
            Review and approve task submissions from users. Track completion statistics and manage rewards.
          </p>
        </Col>
      </Row>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          {getStatsCard('pending', stats.find(s => s._id === 'pending'))}
        </Col>
        <Col md={3}>
          {getStatsCard('approved', stats.find(s => s._id === 'approved'))}
        </Col>
        <Col md={3}>
          {getStatsCard('rejected', stats.find(s => s._id === 'rejected'))}
        </Col>
        <Col md={3}>
          {getStatsCard('under_review', stats.find(s => s._id === 'under_review'))}
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Sort By</Form.Label>
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="submissionDate">Submission Date</option>
                  <option value="rewardAmount">Reward Amount</option>
                  <option value="status">Status</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Order</Form.Label>
                <Form.Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={() => {
                setStatusFilter('all');
                setSortBy('submissionDate');
                setSortOrder('desc');
                setCurrentPage(1);
              }}>
                <i className="fas fa-refresh me-2"></i>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Submissions Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Task Submissions</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading submissions...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Task & User</th>
                  <th>Submission</th>
                  <th>Proof Files</th>
                  <th>Status</th>
                  <th>Reward</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(submission => (
                  <tr key={submission._id}>
                    <td>
                      <div>
                        <strong>{submission.taskId.title}</strong>
                        <div className="small text-muted">
                          {getPriorityBadge(submission.taskId.priority)}
                          <span className="ms-2">{submission.taskId.category}</span>
                        </div>
                        <div className="small">
                          <strong>{submission.userId?.name || 'Unknown User'}</strong>
                          <span className="ms-2">{submission.userId ? getUserTypeBadge(submission.userId.userType) : <Badge bg="secondary">Unknown</Badge>}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ maxWidth: '200px' }}>
                        <div className="text-truncate">
                          {submission.submissionText}
                        </div>
                        {submission.completionTime && (
                          <small className="text-muted">
                            <i className="fas fa-clock me-1"></i>
                            {submission.completionTime} min
                          </small>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {submission.proofFiles.length > 0 ? (
                          <div>
                            <span className="badge bg-info">
                              {submission.proofFiles.length} file{submission.proofFiles.length !== 1 ? 's' : ''}
                            </span>
                            <div className="mt-1">
                              {submission.proofFiles.slice(0, 3).map((file, index) => (
                                <Button
                                  key={index}
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1 mb-1"
                                  onClick={() => openFilePreview(file.filename)}
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  {file.originalName.substring(0, 8)}...
                                </Button>
                              ))}
                              {submission.proofFiles.length > 3 && (
                                <small className="text-muted">+{submission.proofFiles.length - 3} more</small>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">No files</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {getStatusBadge(submission.status)}
                        {submission.reviewedBy && (
                          <div className="small text-muted mt-1">
                            by {submission.reviewedBy.name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>₹{submission.rewardAmount}</strong>
                        <div className="small">
                          <Badge bg={submission.rewardStatus === 'credited' ? 'success' : 'warning'}>
                            {submission.rewardStatus}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td>
                      <small>
                        {formatDate(submission.submissionDate)}
                      </small>
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openReviewModal(submission)}
                      >
                        <i className="fas fa-edit me-1"></i>
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Button
            variant="outline-primary"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            className="me-2"
          >
            Previous
          </Button>
          <span className="align-self-center mx-3">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Review Task Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubmission && (
            <>
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Task Details</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={8}>
                      <h5>{selectedSubmission.taskId.title}</h5>
                      <div className="mb-2">
                        {getPriorityBadge(selectedSubmission.taskId.priority)}
                        <span className="ms-2">{selectedSubmission.taskId.category}</span>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <div className="h5 text-success">₹{selectedSubmission.rewardAmount}</div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">User Submission</h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>User:</strong> {selectedSubmission.userId?.name || 'Unknown User'} ({selectedSubmission.userId?.email || 'No email'})
                    <span className="ms-2">{selectedSubmission.userId ? getUserTypeBadge(selectedSubmission.userId.userType) : <Badge bg="secondary">Unknown</Badge>}</span>
                  </div>
                  <div className="mb-2">
                    <strong>Submitted:</strong> {formatDate(selectedSubmission.submissionDate)}
                  </div>
                  {selectedSubmission.completionTime && (
                    <div className="mb-2">
                      <strong>Time taken:</strong> {selectedSubmission.completionTime} minutes
                    </div>
                  )}
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <div className="bg-light p-3 rounded mt-1">
                      {selectedSubmission.submissionText}
                    </div>
                  </div>
                  
                  {selectedSubmission.proofFiles.length > 0 && (
                    <div>
                      <strong>Proof Files:</strong>
                      <div className="mt-2">
                        {selectedSubmission.proofFiles.map((file, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openFilePreview(file.filename)}
                              className="me-2"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View
                            </Button>
                            <div>
                              <div>{file.originalName}</div>
                              <small className="text-muted">
                                {formatFileSize(file.size)} • {file.mimetype}
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Review Decision</strong></Form.Label>
                  <Form.Select
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    required
                  >
                    <option value="">Select decision...</option>
                    <option value="approved">Approve (Credit reward)</option>
                    <option value="rejected">Reject (No reward)</option>
                    <option value="under_review">Mark for further review</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Review Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add notes about your review decision..."
                    maxLength={500}
                  />
                  <Form.Text className="text-muted">
                    {reviewNotes.length}/500 characters
                  </Form.Text>
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
            Cancel
          </Button>
          <Button
            variant={reviewStatus === 'approved' ? 'success' : reviewStatus === 'rejected' ? 'danger' : 'primary'}
            onClick={handleReview}
            disabled={!reviewStatus || reviewLoading}
          >
            {reviewLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Save Review
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Preview Modal */}
      <Modal show={showFileModal} onHide={() => {
        setShowFileModal(false);
        if (selectedFileBlob) {
          URL.revokeObjectURL(selectedFileBlob);
          setSelectedFileBlob('');
        }
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>File Preview - {selectedFile}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {fileLoading ? (
            <div className="p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading file...</p>
            </div>
          ) : selectedFileBlob ? (
            <img 
              src={selectedFileBlob} 
              alt="Proof file" 
              style={{ maxWidth: '100%', maxHeight: '500px' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.removeAttribute('style');
              }}
            />
          ) : null}
          <div style={{ display: 'none' }} className="text-muted">
            <i className="fas fa-file-alt" style={{ fontSize: '3rem' }}></i>
            <p>File preview not available. This might be a document file.</p>
            {selectedFileBlob && (
              <a href={selectedFileBlob} download={selectedFile} className="btn btn-primary">
                <i className="fas fa-download me-2"></i>
                Download File
              </a>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminTaskReviewDashboard;